<template lang="pug">
div
  b-card
    b-row.mb-1
      b-col(md="8")
        h4 {{ $t('arsopbl.templates_title') }}
        p.text-muted {{ $t('arsopbl.templates_subtitle') }}
      b-col(md="4", class="text-right")
        b-button(v-if="canCreate", variant="primary", @click="openCreate")
          feather-icon(icon="PlusIcon", class="mr-50")
          | {{ $t('arsopbl.new_template') }}

    b-table(
      :items="templates",
      :fields="fields",
      hover,
      responsive,
      show-empty="",
      :empty-text="$t('arsopbl.no_templates')",
      :busy="isLoading"
    )
      template(#table-busy)
        div.text-center.my-3
          loading(:active="true", :is-full-page="false", color="#498ceb")

      template(#cell(rightCode)="data")
        b-badge(v-if="data.item.rightCode", variant="light-primary")
          | {{ $t(`arsopbl.right_${data.item.rightCode.toLowerCase()}`) }}
        b-badge(v-else, variant="light-secondary")
          | {{ $t('arsopbl.template_all_rights') }}

      template(#cell(actions)="data")
        edit-button(v-if="canEdit", @clicked="openEdit(data.item)")
        delete-button(v-if="canDelete", v-b-modal="`modal-delete-${data.item._id}`")
        b-modal(
          :id="`modal-delete-${data.item._id}`",
          centered,
          no-close-on-backdrop,
          modal-class="modal-danger",
          ok-variant="danger",
          cancel-variant="outline-secondary",
          :title="$t('message.confirm_action')",
          :ok-title="$t('message.delete')",
          :cancel-title="$t('message.cancel')",
          @ok="handleDelete(data.item)"
        )
          | {{ $t('arsopbl.confirm_delete_template') }}

  //- Create/Edit modal
  b-modal(
    v-model="showModal",
    :title="editingId ? $t('arsopbl.edit_template') : $t('arsopbl.new_template')",
    size="lg",
    scrollable,
    :ok-title="$t('message.submit')",
    :cancel-title="$t('message.cancel')",
    :ok-disabled="isSubmitting || !form.name.trim() || !form.content.trim()",
    @ok.prevent="handleSubmit"
  )
    b-form-group(:label="$t('arsopbl.template_name')")
      b-form-input(v-model="form.name", :placeholder="$t('arsopbl.template_name_placeholder')")
    b-form-group(:label="$t('arsopbl.template_right')")
      v-select(
        v-model="form.rightCode",
        :options="rightOptions",
        :reduce="opt => opt.value",
        label="text",
        :placeholder="$t('arsopbl.template_all_rights')"
      )
    b-form-group(:label="$t('arsopbl.template_content')")
      quill-editor(ref="quillRef", v-model="form.content", :options="editorOption")

    //- Available dynamic variables
    b-alert(variant="info", show, class="mt-1 mb-0")
      .p-1
        strong {{ $t('arsopbl.template_variables_title') }}
        p.mb-1.mt-25.small {{ $t('arsopbl.template_variables_help') }}
        div
          b-badge.mr-50.mb-25(
            v-for="v in availableVariables",
            :key="v",
            variant="light-info",
            style="cursor: pointer; font-family: monospace;",
            @click="insertVariable(v)"
          ) {{ varLabel(v) }}
</template>

<script>
import {
  ref, reactive, computed, onMounted,
} from '@vue/composition-api'
import { BBadge, BTable, BModal, BAlert, VBModal } from 'bootstrap-vue'
import vSelect from 'vue-select'
import i18n from '@/libs/i18n'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import useCommonArsopbl, { RIGHTS } from '../useCommonArsopbl'
import EditButton from '@/views/components/Shared/Buttons/EditButton.vue'
import DeleteButton from '@/views/components/Shared/Buttons/DeleteButton.vue'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

export default {
  components: { BBadge, BTable, BModal, BAlert, vSelect, EditButton, DeleteButton, quillEditor, Loading },
  directives: { 'b-modal': VBModal },
  setup() {
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const { getTemplates, createTemplate, updateTemplate, deleteTemplate } = useCommonArsopbl()

    const availableVariables = [
      'caseNumber',
      'titular.nombre',
      'titular.identificacion',
      'titular.email',
      'titular.telefono',
      'derecho',
      'fecha',
      'fechaRecepcion',
    ]

    const varLabel = variable => `{{${variable}}}`

    const quillRef = ref(null)

    const insertVariable = (variable) => {
      const text = varLabel(variable)
      const quill = quillRef.value?.quill
      if (!quill) {
        // Fallback: append at the end
        form.content += text
        return
      }
      const range = quill.getSelection(true)
      const index = range ? range.index : quill.getLength()
      quill.insertText(index, text, 'user')
      quill.setSelection(index + text.length, 0)
    }

    const templates = ref([])
    const isLoading = ref(true)
    const isSubmitting = ref(false)
    const showModal = ref(false)
    const editingId = ref(null)
    const canCreate = computed(() => hasPermission('privacy-arsopbl-templates', 'create'))
    const canEdit = computed(() => hasPermission('privacy-arsopbl-templates', 'edit'))
    const canDelete = computed(() => hasPermission('privacy-arsopbl-templates', 'delete'))

    const form = reactive({ name: '', rightCode: null, content: '' })

    const rightOptions = RIGHTS.map(r => ({ value: r.code, text: `${i18n.t(r.labelKey)} (${r.code})` }))

    const editorOption = {
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, false] }],
          ['link'],
          ['clean'],
        ],
      },
      placeholder: i18n.t('arsopbl.template_content_placeholder'),
    }

    const fields = [
      { key: 'actions', label: i18n.t('message.tableHeader.actions') },
      { key: 'name', label: i18n.t('arsopbl.template_name'), sortable: true },
      { key: 'rightCode', label: i18n.t('arsopbl.template_right'), sortable: true },
    ]

    const fetchTemplates = async () => {
      isLoading.value = true
      try {
        templates.value = await getTemplates() || []
      } catch (error) {
        console.log(error)
      } finally {
        isLoading.value = false
      }
    }

    const resetForm = () => {
      form.name = ''
      form.rightCode = null
      form.content = ''
      editingId.value = null
    }

    const openCreate = () => {
      resetForm()
      showModal.value = true
    }

    const openEdit = (item) => {
      editingId.value = item._id.toString()
      form.name = item.name
      form.rightCode = item.rightCode || null
      form.content = item.content
      showModal.value = true
    }

    const handleSubmit = async () => {
      const action = editingId.value ? 'edit' : 'create'
      if (!hasPermission('privacy-arsopbl-templates', action)) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }
      isSubmitting.value = true
      try {
        if (editingId.value) {
          await updateTemplate(editingId.value, { ...form })
          showSuccessMessage(i18n.t('arsopbl.template_updated'))
        } else {
          await createTemplate({ ...form })
          showSuccessMessage(i18n.t('arsopbl.template_created'))
        }
        showModal.value = false
        resetForm()
        await fetchTemplates()
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_template_save'))
      } finally {
        isSubmitting.value = false
      }
    }

    const handleDelete = async (item) => {
      if (!hasPermission('privacy-arsopbl-templates', 'delete')) {
        showErrorMessage(i18n.t('message.you_are_not_authorized'))
        return
      }
      try {
        await deleteTemplate(item._id.toString())
        showSuccessMessage(i18n.t('arsopbl.template_deleted'))
        await fetchTemplates()
      } catch (error) {
        console.log(error)
        showErrorMessage(i18n.t('arsopbl.error_template_delete'))
      }
    }

    onMounted(fetchTemplates)

    return {
      templates,
      isLoading,
      isSubmitting,
      showModal,
      editingId,
      canCreate,
      canEdit,
      canDelete,
      form,
      rightOptions,
      editorOption,
      fields,
      availableVariables,
      insertVariable,
      varLabel,
      quillRef,
      openCreate,
      openEdit,
      handleSubmit,
      handleDelete,
    }
  },
}
</script>
